// district-map.ts — the SYNCED REPORT MAP for the SSR district template's contained side card (ALMANAC
// Chunk 1; layer quality in ALMANAC-2 Chunk C; ONS/EA-grade heat, legend + camera in ALMANAC-3 Chunk C).
//
// A *curated* embed of the explore engine: it reuses the same maplibre-gl + pmtiles RUNTIME and the same
// vector tiles as the SPA (src/main.ts) — the LSOA `areas` choropleth tile (keyed by real ONS LSOA codes
// E01…, baked median_price + imd_decile), the `overlays` tile (flood zones + green space) and the `pois`
// tile (EV + amenities). It does NOT import main.ts, so the prod SPA is untouched and this stays a small,
// grain-agnostic controller. Built as its own stable-named vite entry (`/assets/district-map.js`)
// referenced by the SSR shell. Data is embedded by the server in <script id="district-map-data"> — the
// server also computes the quantile class breaks (deterministic; no client-side stats). Lazy-inits on
// idle so it never gates first paint (POLISH-8). Exposes window.__districtMap = { setLayer, flyTo }.
//
// Tile zoom ranges (verified from the pmtiles headers, ALMANAC-3): areas z6–13 (so the choropleth paints
// at ANY district-fit zoom — the ALMANAC-2 empties were missing LSOA data, not zoom), overlays z8–13,
// pois z11–16. The camera fits the district so it fills the card (owner) — typically z11+, which brackets
// every layer's source zoom.
import maplibregl from 'maplibre-gl';
import mapCss from 'maplibre-gl/dist/maplibre-gl.css?inline';
import { Protocol } from 'pmtiles';

import { heatRadius, heatWeight } from './heat-scale';

// Inject maplibre's CSS ourselves (?inline → a string) so this standalone entry needs no separate
// stylesheet <link> (which would need a hashed name the SSR shell can't know). One-shot, idempotent.
if (!document.getElementById('mlgl-css')) {
  const s = document.createElement('style'); s.id = 'mlgl-css'; s.textContent = mapCss; document.head.appendChild(s);
}
maplibregl.addProtocol('pmtiles', new Protocol().tile);

// Keep tile versions in sync with main.ts. Baked at build from VITE_API_BASE (staging build bakes the
// staging worker), so the browser reaches the same public origin the CSP whitelists.
const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? 'https://api.acreright.co.uk';
// FINAL-PUSH Fix 3: warm the connection to the tile origin the instant this module loads — the choropleth
// fills come from pmtiles range requests against this host, so opening the TCP/TLS connection now (before
// the map even creates its sources) shaves the cold-connect off the fills' critical path. Only the module
// knows API_BASE (baked from VITE_API_BASE), so the preconnect can't live in the SSR shell head.
try {
  const origin = new URL(API_BASE).origin;
  for (const rel of ['preconnect', 'dns-prefetch']) {
    const l = document.createElement('link'); l.rel = rel; l.href = origin; l.crossOrigin = 'anonymous';
    document.head.appendChild(l);
  }
} catch { /* API_BASE not a URL — skip */ }
const AREAS_URL = `pmtiles://${API_BASE}/tiles/uk-areas-v2026-42.pmtiles`;      // LSOA polygons (area_code, median_price, imd_decile)
const POIS_URL = `pmtiles://${API_BASE}/tiles/uk-pois-v2026-44.pmtiles`;        // EV + amenity pins (cat, lad_code)
const OVERLAYS_URL = `pmtiles://${API_BASE}/tiles/uk-overlays-v2026-45.pmtiles`; // flood zones + green space fills
// GEOMETRY-RUN (2026-07-22): real OS Open Roads centrelines + sale-history property points. New versioned
// files uploaded alongside the live tiles; only this staging bundle references them until promote.
const STREETS_URL = `pmtiles://${API_BASE}/tiles/uk-streets-v2026-46.pmtiles`;  // matched centrelines (s=slug, l=lad, m=median)
const SALES_URL = `pmtiles://${API_BASE}/tiles/uk-sales-v2026-46.pmtiles`;      // sale-history points (u=uprn, y=year, b=band, s=slug)
// BASEMAP (2026-08-28): OS Maps raster, NOT CARTO. CARTO withdrew keyless use of basemaps.cartocdn.com
// and now stamps every unauthenticated tile with a diagonal "API KEY REQUIRED · carto.com/basemaps/apikey"
// watermark — which was rendering across the map on every report page. We already hold an OS Maps API key
// (VITE_OS_MAPS_KEY, same key the SPA explorer's Light/Street/Outdoor styles ride) and OS Light_3857 is a
// clean visual match for CARTO light_all, so it is a drop-in. Falls back to OSM standard tiles when no key
// is present (local dev without .env) — never back to CARTO, which would reintroduce the watermark.
// Attribution changes with the source: see BASE_ATTRIB below and the SSR line in _components/mapdata.ts.
const OS_MAPS_KEY = (import.meta.env.VITE_OS_MAPS_KEY as string | undefined) ?? '';
const BASE_TILES = OS_MAPS_KEY
  ? [`https://api.os.uk/maps/raster/v1/zxy/Light_3857/{z}/{x}/{y}.png?key=${OS_MAPS_KEY}`]
  : ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'];
const BASE_ATTRIB = OS_MAPS_KEY
  ? 'Contains OS data &copy; Crown copyright'
  : '&copy; OpenStreetMap contributors';
// OS raster has no maxzoom cap; the OSM fallback stops at 19. A literal `maxzoom: undefined` makes
// MapLibre's style validation reject the source and silently stall style load, so only set it when real.
const BASE_MAXZOOM = OS_MAPS_KEY ? undefined : 19;

// ROBUST-INIT fix #3 (blank-map, docs/HANDOFF-blank-report-map-2026-07-22.md): warm the pmtiles HEADER
// range-requests the instant this module loads. Each vector source (areas/pois/overlays) opens with a
// range-request for the file header + root directory (the first ~16 KB); on a cold R2 object that header
// fetch is what stalls and delays the vector layers. A pre-emptive same-Range fetch primes Cloudflare's
// edge cache + a warm TCP/TLS connection, so by the time the map's sources request the header it's hot —
// cutting how often the init race is lost. Best-effort: any error (the offline e2e harness aborts
// /tiles/**, CORS, cold-start) is swallowed — it only ever helps, never blocks.
function warmTiles(): void {
  for (const u of [AREAS_URL, POIS_URL, OVERLAYS_URL]) {
    try {
      const href = u.replace(/^pmtiles:\/\//, '');
      void fetch(href, { headers: { Range: 'bytes=0-16383' } }).catch(() => { /* best-effort warmup */ });
    } catch { /* fetch unavailable — skip */ }
  }
}
try { warmTiles(); } catch { /* noop */ }

interface Pin { lng: number; lat: number; name: string; sub?: string; tone?: string; weight?: number; kind?: string; phase?: string; ograde?: string; href?: string | null }
interface Nbhd { code: string; name: string; price: number | null; lsoas: string[]; path?: string | null }
interface MapFlags { hasEv?: boolean; hasFlood?: boolean; hasGreen?: boolean; amenFood?: boolean; amenShops?: boolean; amenLeisure?: boolean; crimeSparse?: boolean; hasNoise?: boolean }
interface MapData {
  code: string; name: string; center: [number, number];
  lsoas: string[];
  price: { breaks: number[]; classes: number };
  neighbourhoods: Nbhd[];
  schools: Pin[]; health: Pin[]; stations: Pin[]; crime: Pin[];
  // ALMANAC-6 B: whole-district anonymised crime points (police.uk boundary sample) drive the density
  // HEAT layer, so it spreads across the whole district — not just the top-5 town-centre clusters. The
  // clickable `crime` clusters stay the labelled "on or near" hotspots.
  crimeHeat?: { lng: number; lat: number }[];
  flags?: MapFlags;
  // ALMANAC-5 D3: per-LSOA IMD sub-domain deciles (income/employment/education/health) → the
  // Who-lives-here view switcher. Only views with real data are present.
  imdViews?: Record<string, Record<string, number>>;
  // DEMPROP-A3: per-LSOA CONTINUOUS demographic views (age / tenure / density) → the same Who-lives-here
  // switcher. Raw values (not deciles) → quantile value-ramp. Only views with real data are present.
  demogViews?: Record<string, Record<string, number>>;
  // FINAL-PUSH Fix 2: a sub-grain's OWN footprint. `lsoas` → fit + outline that footprint (neighbourhood);
  // `point`+`zoom` → fit a representative point (street/sector); `marker` → drop a "you are here" pin
  // (street). `label`/`kind` drive the grain-aware chip so it never claims "the district, one level down"
  // on a sub-grain page. Absent → district behaviour (fit the whole boundary). Never carries street/sale
  // geometry.
  // GEOMETRY-RUN: a street page adds streetLad/streetSlug → the client highlights that street's OS Open
  // Roads centreline (uk-streets tile) and pins its sale-history homes (uk-sales tile), both filtered by
  // (l=lad, s=slug). A sector page emits lsoas (reusing the neighbourhood self-outline). streetsLayer on
  // the top-level payload turns on the neighbourhood "colour streets by median" layer (Chunk C3).
  self?: { lsoas?: string[]; point?: [number, number]; zoom?: number; marker?: boolean; label?: string; kind?: string; streetLad?: string; streetSlug?: string };
  // FINAL-PUSH Fix 5: per-LSOA report-unit name + scoped path for EVERY district LSOA (named
  // neighbourhood or singleton/unpriced LSOA), so a choropleth click names + links any polygon.
  lsoaMeta?: Record<string, { name: string; path: string; median?: number | null }>;
  // GEOMETRY-RUN: a street page's own recorded sales, keyed by uprn (a=address, p=price, d=date,
  // h=plot-check permalink), so a sale-pin popover shows the real address + "Plot Check →" without a
  // fetch. Pins whose uprn isn't here (spatial-only match) fall back to the tile's year/band.
  saleMeta?: Record<string, { a?: string; p?: number; d?: string; h?: string }>;
  // GEOMETRY-RUN C3: present + true on a neighbourhood/district map that should offer the "streets shaded
  // by their median" quick-control (the uk-streets tile carries our per-street median).
  streetsLayer?: boolean;
}

// --- Palette. The almanac ramps (MAP BUILD SPEC): ONE sequential single-family ramp per layer,
// light→dark = less→more. RAMP_CLAY for metrics where high = worse/dearer (price, deprivation);
// RAMP_PINE for high = good (kept for reuse). Light ends sit near paper so low values recede.
const RAMP_CLAY = ['#f6efe3', '#f0d7b6', '#e2a76e', '#cd7343', '#9c4a26'];
const RAMP_PINE = ['#f2efe4', '#d8e5d5', '#a3c7a7', '#5f9b73', '#1e5c45'];
const PINE = '#1e5c45', GOOD = '#2e7d5b', MID = '#c9a24a', BAD = '#c0563e', INK = '#20281f';
const GREEN_FILL = '#4caf50', FLOOD3 = '#2c7fb8', FLOOD2 = '#7fcdbb', EV_COLOR = '#27ae60', AMEN_COLOR = '#e6550d';
// ALMANAC-4 D5/D7: the "no data" fill — a neutral paper-grey for a district LSOA that genuinely has no
// value baked (so a gap reads as explained, never as bare basemap = broken). Legend carries a note.
const NODATA = '#ded8ca';
const TONE: Record<string, string> = { good: GOOD, mid: MID, bad: BAD, pine: PINE, clay: '#c96f4a', ink: INK };
void RAMP_PINE; // reserved for high=good choropleths (schools %, broadband) at other grains

// Subsample a 5-stop ramp down to k stops (a district with <10 children drops to 4/3 classes).
function rampFor(base: string[], k: number): string[] {
  if (k >= base.length) return base.slice();
  if (k <= 1) return [base[base.length - 1]];
  const out: string[] = [];
  for (let i = 0; i < k; i++) out.push(base[Math.round((i * (base.length - 1)) / (k - 1))]);
  return out;
}
const gbpK = (v: number): string => '£' + Math.round(v / 1000) + 'k';
// class index for a price given the sorted break boundaries (count how many it exceeds).
function classOf(price: number, breaks: number[]): number { let c = 0; for (const b of breaks) if (price > b) c++; return c; }

// ALMANAC-5 D3 — the Who-lives-here view switcher. Deciles run 1 (most deprived 10%) → 10 (least
// deprived); light→dark = less→more deprived (decile 10 lightest clay, 1 darkest), matching the IMD
// default. `decileColor` mirrors the tile-baked IMD step so every view reads the same way.
function decileColor(d: number): string {
  if (!isFinite(d) || d <= 0) return NODATA;
  if (d < 3) return RAMP_CLAY[4];
  if (d < 5) return RAMP_CLAY[3];
  if (d < 7) return RAMP_CLAY[2];
  if (d < 9) return RAMP_CLAY[1];
  return RAMP_CLAY[0];
}
// The default (headline IMD) fill = the tile-baked imd_decile (no per-LSOA payload needed).
function imdBakedExpr(): unknown[] {
  return ['case', ['>', ['coalesce', ['to-number', ['get', 'imd_decile']], 0], 0],
    ['step', ['coalesce', ['to-number', ['get', 'imd_decile']], 5],
      RAMP_CLAY[4], 3, RAMP_CLAY[3], 5, RAMP_CLAY[2], 7, RAMP_CLAY[1], 9, RAMP_CLAY[0]], NODATA];
}
// A sub-domain view (income/employment/education/health) colours each LSOA by its held decile via a
// code→colour match; an LSOA with no value falls to NODATA grey.
function imdViewExpr(view: Record<string, number>): unknown[] {
  const expr: unknown[] = ['match', ['get', 'area_code']];
  for (const [code, d] of Object.entries(view)) expr.push(code, decileColor(d));
  expr.push(NODATA);
  return expr;
}
// DEMPROP-A3 — continuous demographic views (age / tenure / density) on the SAME Who-lives-here layer.
// These are RAW values, not deciles, so they use a quantile VALUE ramp (not decileColor). Neutral
// single-family clay ramp (RAMP_PINE stays reserved for high=good — age/density/tenure aren't good/bad).
const DEMOG_VIEWS = ['age', 'tenure', 'density'] as const;
const DEMOG_META: Record<string, { chip: string; legend: string; fmt: (v: number) => string }> = {
  age: { chip: 'Median age', legend: 'Median age (years) · ONS Census 2021', fmt: (v) => `${Math.round(v)} yrs` },
  tenure: { chip: '% owner-occupied', legend: 'Owner-occupied homes (%) · ONS Census 2021', fmt: (v) => `${Math.round(v)}% owner-occupied` },
  density: { chip: 'People per km²', legend: 'Population density (per km²) · ONS Census 2021', fmt: (v) => `${Math.round(v).toLocaleString()} / km²` },
};
const isDemogView = (v: string): boolean => (DEMOG_VIEWS as readonly string[]).includes(v);
// Quantile class boundaries over the view's values (fewer children → fewer classes, ONS idiom).
function valueBreaks(vals: number[]): number[] {
  const s = vals.filter((v) => isFinite(v)).sort((a, b) => a - b);
  const k = s.length >= 10 ? 5 : s.length >= 5 ? 4 : s.length >= 2 ? 3 : 1;
  const breaks: number[] = [];
  for (let i = 1; i < k; i++) breaks.push(s[Math.floor((i * s.length) / k)]);
  return breaks;
}
// A continuous view colours each LSOA by the quantile class of its value via a code→colour match.
function valueViewExpr(view: Record<string, number>): unknown[] {
  const vals = Object.values(view).filter((v) => isFinite(v));
  const breaks = valueBreaks(vals);
  const ramp = rampFor(RAMP_CLAY, breaks.length + 1);
  const expr: unknown[] = ['match', ['get', 'area_code']];
  for (const [code, v] of Object.entries(view)) {
    if (!isFinite(v)) continue;
    let c = 0; for (const b of breaks) if (v > b) c++;
    expr.push(code, ramp[Math.min(c, ramp.length - 1)]);
  }
  expr.push(NODATA);
  return expr;
}
const IMD_VIEW_LABELS: Record<string, string> = { imd: 'Deprivation (IMD)', income: 'Income', employment: 'Employment', education: 'Education', health: 'Health' };
const IMD_LEGEND_TITLE: Record<string, string> = {
  imd: 'Deprivation — IMD decile', income: 'Income deprivation — decile', employment: 'Employment deprivation — decile',
  education: 'Education deprivation — decile', health: 'Health deprivation — decile',
};

// tab key → { layers to show, chip HTML, legend key }. `layers` names the AUTHORITATIVE set for that
// tab — applyPreset turns every other toggleable layer OFF, so a tab change can never bleed through.
type LegendKind = 'price' | 'imd' | 'crime' | 'environment' | 'amenities' | 'schools' | 'health' | 'transport';
const PRESETS: Record<string, { layers: string[]; chip: string; legend: LegendKind }> = {
  'overview': { layers: ['nbhd-fill', 'nbhd-line'], chip: '<b>neighbourhood prices</b> — the district, one level down', legend: 'price' },
  'cost': { layers: ['nbhd-fill', 'nbhd-line'], chip: '<b>neighbourhood prices</b> — click a neighbourhood to read it', legend: 'price' },
  'safety': { layers: ['crime-heat', 'crime'], chip: '<b>crime density</b> — anonymised “on or near” clusters, not addresses', legend: 'crime' },
  'who-lives-here': { layers: ['imd-fill', 'imd-line'], chip: '<b>deprivation</b> — IMD decile by neighbourhood', legend: 'imd' },
  'schools': { layers: ['schools'], chip: '<b>schools</b> — colour is the latest Ofsted grade', legend: 'schools' },
  'health': { layers: ['health'], chip: '<b>GPs, dentists &amp; pharmacies</b>', legend: 'health' },
  'getting-around': { layers: ['stations', 'pins-ev'], chip: '<b>stations</b> &amp; <b>EV charge points</b>', legend: 'transport' },
  'environment': { layers: ['ov-floodfz3', 'ov-floodfz2', 'ov-greenspace'], chip: '<b>flood zones</b> &amp; <b>green space</b>', legend: 'environment' },
  'amenities': { layers: ['pins-amenities', 'ov-greenspace'], chip: '<b>shops &amp; amenities</b> · green space in fill', legend: 'amenities' },
};
// Every layer applyPreset may toggle. district boundary + base + selection always stay, so they're not here.
const TOGGLE_IDS = ['nbhd-fill', 'nbhd-line', 'imd-fill', 'imd-line', 'ov-floodfz3', 'ov-floodfz2', 'ov-greenspace', 'ov-noise', 'pins-ev', 'pins-amenities', 'schools', 'health', 'stations', 'crime-heat', 'crime', 'nbhd-streets'];
// Point layers that get a hover/tap popup.
const PIN_LAYER_IDS = ['schools', 'health', 'stations', 'crime', 'pins-ev', 'pins-amenities'];
// Choropleth fill layers that get hover-value + click-to-read.
const CHORO_IDS = ['nbhd-fill', 'imd-fill'];

function readData(): MapData | null {
  const el = document.getElementById('district-map-data');
  if (!el?.textContent) return null;
  try { return JSON.parse(el.textContent) as MapData; } catch { return null; }
}

function fc(pins: Array<Partial<Pin> & { lng: number; lat: number }>): GeoJSON.FeatureCollection {
  return { type: 'FeatureCollection', features: pins.filter((p) => isFinite(p.lng) && isFinite(p.lat)).map((p) => ({
    type: 'Feature', geometry: { type: 'Point', coordinates: [p.lng, p.lat] },
    properties: {
      name: p.name ?? '', sub: p.sub ?? '', color: TONE[p.tone ?? 'pine'] ?? PINE, weight: p.weight ?? 1,
      kind: p.kind ?? '', phase: p.phase ?? '', ograde: p.ograde ?? '', href: p.href ?? '',
    },
  })) };
}

let map: maplibregl.Map | null = null;
let started = false;
let DATA: MapData | null = null;
let nbByCode: Record<string, Nbhd> = {};   // code → neighbourhood (row fly-to)
let nbByLsoa: Record<string, Nbhd> = {};   // member LSOA code → its neighbourhood (choropleth click-to-read)
let unitLsoaCount: Record<string, number> = {}; // report-unit path → how many LSOAs it spans (pocket vs whole-unit grain check)
let priceRamp: string[] = [];              // the k-colour clay ramp for the price choropleth
let streetsByMedian = false;               // GEOMETRY-RUN reviewer fix 1: the "Streets by median" mode is on
// nbhd-streets line width: thin default (hidden layer) vs the emphasis ramp used while the mode is on, so
// the shaded centrelines read clearly over the faded choropleth beneath rather than as 1px hairlines.
const STREETS_W_DEFAULT = ['interpolate', ['linear'], ['zoom'], 11, 1, 16, 4] as unknown as maplibregl.DataDrivenPropertyValueSpecification<number>;
const STREETS_W_EMPH = ['interpolate', ['linear'], ['zoom'], 11, 2.5, 16, 6] as unknown as maplibregl.DataDrivenPropertyValueSpecification<number>;
const NBHD_FILL_OPACITY = 0.72, NBHD_FILL_FADED = 0.15; // choropleth fill: full vs faded-under-streets-mode
let selection: string | null = null;       // legend "… in <name>" line for the current click
let imdView = 'imd';                        // Who-lives-here active view (imd | income | employment | education | health)

// Re-colour the imd-fill layer for the active Who-lives-here view (ALMANAC-5 D3).
function setImdView(view: string): void {
  imdView = view;
  if (!map?.getLayer('imd-fill')) return;
  const demog = isDemogView(view) ? DATA?.demogViews?.[view] : null;
  const sub = DATA?.imdViews?.[view];
  const expr = demog ? valueViewExpr(demog)
    : (view === 'imd' || !sub) ? imdBakedExpr() : imdViewExpr(sub);
  map.setPaintProperty('imd-fill', 'fill-color', expr as unknown as maplibregl.DataDrivenPropertyValueSpecification<string>);
  renderLegend();
}

function boot(data: MapData): void {
  const container = document.getElementById('report-map');
  if (!container || map) return;
  DATA = data;
  // GEOMETRY-RUN: does THIS map need the OS-geometry tiles? A street page draws its centreline + sale
  // pins; a neighbourhood/district can opt into the streets-by-median layer. Everything else omits them.
  const isStreet = data.self?.kind === 'street' && !!data.self.streetSlug;
  const wantStreets = isStreet || data.streetsLayer === true;
  nbByCode = {}; nbByLsoa = {};
  for (const n of data.neighbourhoods) { nbByCode[n.code] = n; for (const l of n.lsoas) nbByLsoa[l] = n; }
  // How many LSOAs each report unit spans (via its scoped path) — so a click that reads a single member
  // LSOA's baked median under a MULTI-LSOA unit's name can honestly say "a pocket of <name>" instead of
  // implying it's the unit's own figure (which the unit's page reports as an aggregate).
  unitLsoaCount = {};
  for (const l of Object.keys(data.lsoaMeta ?? {})) { const p = data.lsoaMeta?.[l]?.path; if (p) unitLsoaCount[p] = (unitLsoaCount[p] ?? 0) + 1; }
  // ROBUST-INIT (blank-map fix, docs/HANDOFF-blank-report-map-2026-07-22.md). The pmtiles VECTOR sources
  // (areas/pois/overlays) are NOT put in the initial style. When one of their header range-requests stalled
  // on cold R2 it kept the whole style from reaching a ready state, so the old getSource('base') gate bailed
  // forever and NOTHING — not even the CARTO basemap — ever painted (evidence: zero tile requests on a blank
  // load). Instead we build a MINIMAL, fully-synchronous style (just the `base` raster: explicit tile URLs,
  // no async metadata) and register every other source AFTER the base style is applied (initOnce). `base`
  // then renders unconditionally, and a stalled vector source can only leave ITS OWN layer blank — never the
  // basemap, boundary or crime heat (the ALMANAC-3 intent the old gate undercut).
  const deferredSources: Record<string, maplibregl.SourceSpecification> = {
    areas: { type: 'vector', url: AREAS_URL, promoteId: { areas: 'area_code' }, attribution: 'Contains ONS &amp; HM Land Registry data &copy; Crown copyright' },
    pois: { type: 'vector', url: POIS_URL, attribution: '&copy; OpenStreetMap contributors' },
    overlays: { type: 'vector', url: OVERLAYS_URL, attribution: 'Flood data &copy; Environment Agency; Green spaces &copy; Crown copyright — OS Open Greenspace' },
    districts: { type: 'geojson', data: '/districts-england.geojson', promoteId: 'area_code' },
    schools: { type: 'geojson', data: fc(data.schools) },
    health: { type: 'geojson', data: fc(data.health) },
    stations: { type: 'geojson', data: fc(data.stations) },
    crime: { type: 'geojson', data: fc(data.crime) },
    // ALMANAC-6 B: whole-district points for the heat layer (falls back to the hotspots if the worker
    // didn't supply points — an older cache).
    crimeHeat: { type: 'geojson', data: fc(data.crimeHeat && data.crimeHeat.length ? data.crimeHeat : data.crime) },
    // GEOMETRY-RUN: the OS-geometry vector sources, added ONLY where a layer will use them (a street page,
    // or a neighbourhood that opted into the streets-by-median control) so district maps keep their exact
    // tile-fetch footprint. Attribution rides on the source (shown as card furniture too).
    ...(wantStreets ? { streets: { type: 'vector' as const, url: STREETS_URL, attribution: 'Contains OS data &copy; Crown copyright and database right 2026' } } : {}),
    ...(isStreet ? { sales: { type: 'vector' as const, url: SALES_URL, attribution: 'Contains OS, HM Land Registry data &copy; Crown copyright' } } : {}),
  };
  map = new maplibregl.Map({
    container,
    style: {
      version: 8,
      sources: {
        base: { type: 'raster', tiles: BASE_TILES, tileSize: 256, attribution: BASE_ATTRIB, ...(BASE_MAXZOOM != null ? { maxzoom: BASE_MAXZOOM } : {}) },
      },
      layers: [{ id: 'base', type: 'raster', source: 'base', paint: { 'raster-opacity': 0.9 } }],
    },
    center: data.center,
    zoom: 10.5,
    maxZoom: 17,
    // ALMANAC-5 D1: no ON-canvas attribution. Licences require attribution ADJACENT to the map, not
    // over it — we render it as card furniture below the legend (mapdata.ts reportMapPane).
    attributionControl: false,
    // Report map: quiet controls. Pan/zoom yes; no rotate (keeps it a calm reference, not a GIS tool).
    dragRotate: false, pitchWithRotate: false,
    // ALMANAC-8 B item 2 — cooperative gestures on mobile: a one-finger drag scrolls the PAGE (never
    // hijacked by the map), two fingers pan/zoom the map; the wheel needs ctrl/⌘. Off on desktop where the
    // map is a large sticky panel and drag-to-pan is expected. The full-screen explorer (tap to expand)
    // gives the uninhibited map on mobile.
    cooperativeGestures: typeof window !== 'undefined' && window.matchMedia('(max-width:960px)').matches,
  });
  const m = map;
  m.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
  // Re-measure once the container is definitely laid out (and on any later container resize) — maplibre
  // sizes its canvas to the container at creation, so a late layout would otherwise leave it 0-height.
  // A resize also re-fits the district so it keeps filling the card (owner: re-fit on card resize).
  requestAnimationFrame(() => m.resize());
  if ('ResizeObserver' in window) {
    let t = 0;
    new ResizeObserver(() => { m.resize(); if (inited) { window.clearTimeout(t); t = window.setTimeout(() => fitCamera(m, data), 120); } }).observe(container);
  }
  container.classList.add('loading');

  // Handoff fix #2 — surface the real maplibre/pmtiles/source error instead of a silent blank. A source or
  // tile failure is ISOLATED to that source by maplibre (it never rejects the whole map), so we only LOG it;
  // the basemap + every other layer keep painting. This is what turns a future "blank ITS OWN layer" event
  // from a mystery into a named error in the console.
  m.on('error', (e) => { try { console.error('[dmap] map error:', (e && (e as { error?: unknown }).error) ?? e); } catch { /* noop */ } });

  // Init the moment the (now fully-synchronous) base style is applied — NOT gated on all sources loading.
  // `styledata` fires once the style JSON is parsed, at which point addSource/addLayer are safe; `load` and
  // two hard-timeout backstops (for a throttled background tab where events lag) belt-and-brace it. Because
  // the initial style holds no async source, one of these paths always reaches initOnce quickly — the old
  // failure (a stalled pmtiles vector source starving the gate) can no longer happen.
  m.on('styledata', initOnce);
  m.on('load', initOnce);
  window.setTimeout(initOnce, 800);
  window.setTimeout(initOnce, 2500);
  // Drop the skeleton once we've initialised (or as a last resort after a long wait).
  m.once('idle', () => container.classList.remove('loading'));
  window.setTimeout(() => container.classList.remove('loading'), 12000);

  function initOnce(): void {
    if (inited || !map) return;
    // Register the deferred sources now the base style is up. addSource throws if the style JSON isn't
    // parsed yet — bail quietly and let a later styledata/load/timeout retry (inited stays false). Once a
    // source is REGISTERED, addLayer works immediately, even before that source's tiles/metadata arrive.
    try {
      for (const [id, spec] of Object.entries(deferredSources)) if (!m.getSource(id)) m.addSource(id, spec);
    } catch { return; } // style not ready yet — a later event/timeout retries
    inited = true;
    // Base style + all sources are up. Add every layer, apply the tab preset, fit the camera. A stalled
    // vector source leaves only its own layer blank; base/boundary/crime-heat still paint. (Not retried on
    // an addLayers throw — inited is already set — so a genuine layer bug logs once, never loops.)
    try { addLayers(m, data); applyPreset(currentTab); void fitCamera(m, data); }
    catch (e) { try { console.error('[dmap] init failed:', e); } catch { /* noop */ } }
    container!.classList.remove('loading');
  }
}
let inited = false;

// Camera: fit the district boundary so it fills the card (owner — was "floating in a sea of neighbours").
// The static LAD geojson is authoritative and deterministic; fetching it (cached — it's also the boundary
// source) lets us compute the exact bounds, rather than querySourceFeatures which only sees loaded tiles.
let districtBounds: maplibregl.LngLatBounds | null = null;
async function fitDistrict(m: maplibregl.Map, code: string): Promise<void> {
  if (!districtBounds) {
    try {
      const gj = await fetch('/districts-england.geojson').then((r) => r.json()) as GeoJSON.FeatureCollection;
      const f = gj.features.find((ft) => (ft.properties as { area_code?: string } | null)?.area_code === code);
      if (f) { const b = new maplibregl.LngLatBounds(); forEachCoord(f.geometry, (lng, lat) => b.extend([lng, lat])); districtBounds = b; }
    } catch { /* fall through to a source query */ }
  }
  if (!districtBounds) {
    const feats = m.querySourceFeatures('districts', { filter: ['==', ['get', 'area_code'], code] as unknown as maplibregl.FilterSpecification });
    const b = new maplibregl.LngLatBounds(); let any = false;
    for (const f of feats) forEachCoord(f.geometry, (lng, lat) => { b.extend([lng, lat]); any = true; });
    if (any) districtBounds = b;
  }
  if (districtBounds) m.fitBounds(districtBounds, { padding: 24, duration: 0, maxZoom: 13.5 });
}

// FINAL-PUSH Fix 2: fit the camera to a SUB-GRAIN's own footprint. `self.lsoas` → jump to the area centroid
// at a neighbourhood zoom, then refine to the exact LSOA polygons once the `areas` tiles load (best-effort:
// querySourceFeatures only sees loaded tiles, so the initial jump guarantees a sensible view even if the
// refine finds nothing). `self.point` (street/sector, no own polygon) → fit that representative point at
// the given zoom. The persistent `self-line`/marker are added in addLayers; this only drives the camera.
function fitSelf(m: maplibregl.Map, data: MapData): void {
  const s = data.self!;
  const center = s.point ?? data.center;
  const zoom = s.zoom ?? 13.5;
  m.jumpTo({ center, zoom });
  if (s.lsoas && s.lsoas.length) {
    const refine = (): void => {
      let feats: maplibregl.MapGeoJSONFeature[] = [];
      try { feats = m.querySourceFeatures('areas', { sourceLayer: 'areas', filter: ['in', ['get', 'area_code'], ['literal', s.lsoas]] as unknown as maplibregl.FilterSpecification }); } catch { feats = []; }
      const b = new maplibregl.LngLatBounds(); let any = false;
      for (const f of feats) forEachCoord(f.geometry, (lng, lat) => { b.extend([lng, lat]); any = true; });
      if (any) m.fitBounds(b, { padding: 28, duration: 0, maxZoom: 15 });
    };
    refine();
    m.once('idle', refine);
  }
  // GEOMETRY-RUN: a street fits to its OWN centreline once the streets tile loads (querySourceFeatures only
  // sees loaded tiles, so the initial point jump guarantees a sane first view). If the tile holds no matching
  // centreline the street is unmatched (~15%) — drop the honest centroid marker instead of a fake line, and
  // downgrade the chip. Runs on each idle until it either fits a line or confirms there's none to fit.
  if (s.kind === 'street' && s.streetSlug) {
    const lad = s.streetLad ?? data.code, slug = s.streetSlug;
    const filter = ['all', ['==', ['get', 'l'], lad], ['==', ['get', 's'], slug]] as unknown as maplibregl.FilterSpecification;
    let settled = false;
    const refineStreet = (): void => {
      if (settled || !m.getSource('streets')) return;
      let feats: maplibregl.MapGeoJSONFeature[] = [];
      try { feats = m.querySourceFeatures('streets', { sourceLayer: 'streets', filter }); } catch { feats = []; }
      const b = new maplibregl.LngLatBounds(); let any = false;
      for (const f of feats) forEachCoord(f.geometry, (lng, lat) => { b.extend([lng, lat]); any = true; });
      if (any) { settled = true; m.fitBounds(b, { padding: 40, duration: 0, maxZoom: 16.5 }); }
    };
    refineStreet();
    m.once('idle', () => {
      refineStreet();
      // If nothing matched after the tiles settled AND the source has loaded any street tiles at all,
      // treat the street as unmatched: honest centroid marker + degraded chip.
      if (!settled && s.point && !streetDegraded) {
        streetDegraded = true;
        const el = document.createElement('div'); el.className = 'rm-here';
        el.setAttribute('aria-label', `${data.name} — approximate location`);
        new maplibregl.Marker({ element: el, anchor: 'bottom' }).setLngLat(s.point).addTo(m);
        applyPreset(currentTab); // refresh the chip to the fallback copy
      }
    });
  }
}
// GEOMETRY-RUN: set once fitSelf confirms a street has no OS centreline in the tile → the chip degrades
// from "its street highlighted" to the honest centroid-marker wording.
let streetDegraded = false;

// Dispatch: a sub-grain with a `self` footprint fits to itself; otherwise the district boundary.
function fitCamera(m: maplibregl.Map, data: MapData): void {
  if (data.self && ((data.self.lsoas && data.self.lsoas.length) || data.self.point)) fitSelf(m, data);
  else void fitDistrict(m, data.code);
}

function addLayers(m: maplibregl.Map, data: MapData): void {
  const codeFilter: maplibregl.FilterSpecification = ['==', ['get', 'area_code'], data.code];
  // District boundary fill (whisper) — the choropleths sit above it, the dashed outline above them.
  m.addLayer({ id: 'district-fill', type: 'fill', source: 'districts', filter: codeFilter, paint: { 'fill-color': PINE, 'fill-opacity': 0.03 } });

  // ---- Area choropleths over the LSOA `areas` tile (E01… codes). FULL-COVERAGE fills (ONS idiom): every
  // member LSOA is filled, zero bare basemap inside the boundary. Prices colour each member LSOA by its
  // neighbourhood's quantile class (server-computed breaks); deprivation by the tile-baked IMD decile.
  const nb = data.neighbourhoods.filter((n) => n.lsoas && n.lsoas.length);
  priceRamp = rampFor(RAMP_CLAY, Math.max(2, data.price?.classes ?? 5));
  const breaks = data.price?.breaks ?? [];
  // ALMANAC-5 A — REGRESSION FIX. A-4 filled by ['==',['get','lad_code'],code], but the `areas` vector
  // tile is baked with ONLY area_code/avg_ppsqm/median_price/imd_decile (build_tiles.sh --include list) —
  // it carries NO lad_code, so that filter matched zero features and the whole choropleth rendered EMPTY
  // (the owner's "maps degraded"). Revert to the A-3 approach that demonstrably painted: enumerate the
  // district's LSOA codes (area_code) directly. Full-district coverage (incl. the Tandridge fix) now comes
  // from data.lsoas being the COMPLETE LAD member set (worker returns every area.type='lsoa' in the LAD),
  // not just named-neighbourhood members. An LSOA with no baked price still fills → NODATA grey.
  const districtFilter = ['in', ['get', 'area_code'], ['literal', data.lsoas ?? []]] as unknown as maplibregl.FilterSpecification;
  // Baked per-LSOA median_price → quantile class (the ONS step). An LSOA with no baked price → NODATA
  // grey (explained gap, never bare basemap).
  // A maplibre `step` needs at least ONE stop; with breaks:[] the old `['step', input, ramp0]` had zero
  // stops → maplibre rejected the whole paint expression and DROPPED the nbhd-fill layer (surfaced by the
  // new m.on('error') on a price-stripped tab payload e.g. the Safety route, and it would silently kill the
  // MAIN choropleth on any district whose price data yields empty breaks). Guard to a flat first-class
  // colour when there are no breaks so the expression is always valid.
  let bakedByPrice: unknown = priceRamp[0];
  if (breaks.length) {
    const step: unknown[] = ['step', ['coalesce', ['to-number', ['get', 'median_price']], 0], priceRamp[0]];
    for (let i = 0; i < breaks.length; i++) step.push(breaks[i], priceRamp[Math.min(priceRamp.length - 1, i + 1)]);
    bakedByPrice = step;
  }
  const priceBase: unknown[] = ['case', ['>', ['coalesce', ['to-number', ['get', 'median_price']], 0], 0], bakedByPrice, NODATA];
  // Colour each LSOA by its report UNIT'S OWN median wherever we hold it — a priced neighbourhood's
  // aggregate (n.price) for its members, else the singleton LSOA's own prices-theme median (lsoaMeta) —
  // so the map colour, the click popover and the unit's page all tell the SAME number (not the tile-baked
  // median_price, which can differ by window/vintage). Everything else falls through to the baked base.
  const priced = nb.filter((n) => n.price != null);
  const colourByLsoa = new Map<string, string>();
  const clay = (median: number): string => priceRamp[Math.min(priceRamp.length - 1, classOf(median, breaks))];
  for (const n of priced) for (const l of n.lsoas) colourByLsoa.set(l, clay(n.price as number));
  for (const [lsoa, meta] of Object.entries(data.lsoaMeta ?? {})) {
    if (meta.median != null && !colourByLsoa.has(lsoa)) colourByLsoa.set(lsoa, clay(meta.median));
  }
  let priceColor: unknown[] = priceBase;
  if (colourByLsoa.size) {
    const priceExpr: unknown[] = ['match', ['get', 'area_code']];
    for (const [l, colour] of colourByLsoa) priceExpr.push(l, colour);
    priceExpr.push(priceBase);
    priceColor = priceExpr;
  }
  m.addLayer({ id: 'nbhd-fill', type: 'fill', source: 'areas', 'source-layer': 'areas', filter: districtFilter, layout: { visibility: 'none' },
    paint: { 'fill-color': priceColor as unknown as maplibregl.DataDrivenPropertyValueSpecification<string>, 'fill-opacity': NBHD_FILL_OPACITY } }, 'district-fill');
  // Hairline paper-white strokes between polygons (ONS rule 4).
  m.addLayer({ id: 'nbhd-line', type: 'line', source: 'areas', 'source-layer': 'areas', filter: districtFilter, layout: { visibility: 'none' },
    paint: { 'line-color': '#faf7f1', 'line-width': 0.75 } });

  // Deprivation choropleth (Who lives here): tile-baked imd_decile over the WHOLE district (lad_code).
  // deciles are already classes; light→dark = less→more deprived (decile 10 light, decile 1 darkest
  // clay); an LSOA with no baked decile → NODATA grey (the Tandridge "missing IMD for some codes" case).
  m.addLayer({ id: 'imd-fill', type: 'fill', source: 'areas', 'source-layer': 'areas', filter: districtFilter, layout: { visibility: 'none' },
    paint: {
      'fill-color': imdBakedExpr() as unknown as maplibregl.DataDrivenPropertyValueSpecification<string>,
      'fill-opacity': 0.72,
    } }, 'district-fill');
  m.addLayer({ id: 'imd-line', type: 'line', source: 'areas', 'source-layer': 'areas', filter: districtFilter, layout: { visibility: 'none' },
    paint: { 'line-color': '#faf7f1', 'line-width': 0.75 } });

  // Selected-area outline (dark ink) — the click-to-read highlight. Empty until a click. Always present,
  // above the fills; never toggled by presets.
  m.addLayer({ id: 'sel-line', type: 'line', source: 'areas', 'source-layer': 'areas',
    filter: ['in', ['get', 'area_code'], ['literal', []]] as unknown as maplibregl.FilterSpecification,
    paint: { 'line-color': INK, 'line-width': 2.4 } });

  // FINAL-PUSH Fix 2: a sub-grain's OWN footprint, PERSISTENT (applyPreset never clears it, unlike the
  // click sel-line) — the neighbourhood's own member LSOAs, outlined dark so the reader sees exactly which
  // area the report is about while the sibling choropleth gives peer-price context. Sits above the fills.
  if (data.self?.lsoas && data.self.lsoas.length) {
    m.addLayer({ id: 'self-line', type: 'line', source: 'areas', 'source-layer': 'areas',
      filter: ['in', ['get', 'area_code'], ['literal', data.self.lsoas]] as unknown as maplibregl.FilterSpecification,
      paint: { 'line-color': INK, 'line-width': 2.8 } });
  }
  // GEOMETRY-RUN Chunk C — a STREET page draws its real OS Open Roads centreline (highlighted, persistent)
  // and pins its own sale-history homes from the uk-sales tile, both filtered by (l=lad, s=slug). The pins
  // sit UNDER the centreline so the line reads as the subject. If the tile holds no matching centreline
  // (an unmatched street, ~15%), fitSelf falls back to the centroid marker below — honestly, no fake line.
  const st = data.self;
  if (st?.kind === 'street' && st.streetSlug && m.getSource('streets')) {
    const lad = st.streetLad ?? data.code, slug = st.streetSlug;
    // The centreline (uk-streets) carries l+s; the sale pins (uk-sales) carry only s (a street page is zoomed
    // to one street, so there's no cross-LAD same-slug collision in view — omitting lad keeps 3M points lean).
    const lineFilter = ['all', ['==', ['get', 'l'], lad], ['==', ['get', 's'], slug]] as unknown as maplibregl.FilterSpecification;
    const pinFilter = ['==', ['get', 's'], slug] as unknown as maplibregl.FilterSpecification;
    if (m.getSource('sales')) {
      m.addLayer({ id: 'street-sales', type: 'circle', source: 'sales', 'source-layer': 'sales', filter: pinFilter,
        paint: { 'circle-radius': ['interpolate', ['linear'], ['zoom'], 13, 3, 17, 6.5], 'circle-color': '#c96f4a',
          'circle-opacity': 0.92, 'circle-stroke-color': '#fff', 'circle-stroke-width': 1.1 } });
    }
    m.addLayer({ id: 'self-street', type: 'line', source: 'streets', 'source-layer': 'streets', filter: lineFilter,
      paint: { 'line-color': INK, 'line-width': ['interpolate', ['linear'], ['zoom'], 12, 2.5, 16, 5.5], 'line-opacity': 0.92 } });
  }
  // GEOMETRY-RUN Chunk C3 — the neighbourhood/district "streets shaded by their median" layer: every matched
  // centreline in this district, coloured by our per-street median on the same clay ramp/breaks as the price
  // choropleth. Hidden by default; a quick-control on the price tabs toggles it (the grain-ladder vision:
  // area → street → sale). Only added when the assembler opted in (streetsLayer) and the source is present.
  if (data.streetsLayer && m.getSource('streets')) {
    const breaks = data.price?.breaks ?? [];
    const ramp = rampFor(RAMP_CLAY, Math.max(2, data.price?.classes ?? 5));
    // Same zero-stop guard as nbhd-fill: an empty-breaks `step` is invalid and would drop this layer.
    let byMedian: unknown = ramp[0];
    if (breaks.length) {
      const step: unknown[] = ['step', ['coalesce', ['to-number', ['get', 'm']], 0], ramp[0]];
      for (let i = 0; i < breaks.length; i++) step.push(breaks[i], ramp[Math.min(ramp.length - 1, i + 1)]);
      byMedian = step;
    }
    const colour: unknown[] = ['case', ['>', ['coalesce', ['to-number', ['get', 'm']], 0], 0], byMedian, '#b8b1a2'];
    m.addLayer({ id: 'nbhd-streets', type: 'line', source: 'streets', 'source-layer': 'streets',
      filter: ['==', ['get', 'l'], data.code] as unknown as maplibregl.FilterSpecification, minzoom: 11, layout: { visibility: 'none' },
      paint: { 'line-color': colour as unknown as maplibregl.DataDrivenPropertyValueSpecification<string>,
        'line-width': STREETS_W_DEFAULT, 'line-opacity': 0.85 } });
  }
  // A street/sector with no drawn geometry falls back to a single "you are here" marker at the representative
  // point. For a matched street the centreline above replaces it, so the marker is added post-load ONLY if
  // the centreline turns out empty (see fitSelf). Sectors/others still get it here when asked.
  if (data.self?.marker && data.self.point && data.self.kind !== 'street') {
    const el = document.createElement('div');
    el.className = 'rm-here';
    el.setAttribute('aria-label', `${data.name} — approximate location`);
    new maplibregl.Marker({ element: el, anchor: 'bottom' }).setLngLat(data.self.point).addTo(m);
  }

  // District boundary outline ON TOP (dashed pine), so shading reads inside a clear border.
  m.addLayer({ id: 'district-line', type: 'line', source: 'districts', filter: codeFilter, paint: { 'line-color': PINE, 'line-width': 2, 'line-dasharray': [3, 2] } });

  // ---- Overlay fills (Environment + Amenities green): EA-style banded classes, solid semi-transparent
  // fills over the ghosted basemap (rule 7). Flood zone 3 deeper blue, zone 2 lighter, green space green.
  const ov = (id: string, srcLayer: string, color: string, opacity: number): void => {
    m.addLayer({ id, type: 'fill', source: 'overlays', 'source-layer': srcLayer, minzoom: 8, layout: { visibility: 'none' },
      paint: { 'fill-color': color, 'fill-opacity': opacity, 'fill-outline-color': color } });
  };
  ov('ov-floodfz3', 'floodfz3', FLOOD3, 0.45);
  ov('ov-floodfz2', 'floodfz2', FLOOD2, 0.30);
  ov('ov-greenspace', 'greenspace', GREEN_FILL, 0.35);
  // ALMANAC-6 C — Defra transport-noise band corridors (Environment tab). Pre-wired: reads the `noise`
  // source-layer of the overlays tile, coloured by the band's lower-bound dB (Lden) on the standard
  // Defra ramp (55→59 yellow · 60→64 amber · 65→69 orange · 70+ deep red). Until the overlays tile is
  // rebuilt with a `noise` source-layer (m1_noise.py's tile step), this layer simply has no features —
  // no error, nothing drawn. The Environment quick-control chip that toggles it only appears where the
  // area actually holds mapped noise (flags.hasNoise), so there's never a dead control.
  m.addLayer({ id: 'ov-noise', type: 'fill', source: 'overlays', 'source-layer': 'noise', minzoom: 8, layout: { visibility: 'none' },
    paint: {
      'fill-color': ['step', ['coalesce', ['to-number', ['get', 'band']], 55], '#F2D06B', 60, '#EF9F27', 65, '#DD7A2E', 70, '#B23A2E'],
      'fill-opacity': 0.5, 'fill-outline-color': '#B23A2E',
    } });

  // ---- POI pins (EV + amenities) from the pois tile (z11–16), scoped to THIS district by lad_code.
  const ladFilter = ['==', ['get', 'lad_code'], data.code] as unknown as maplibregl.FilterSpecification;
  m.addLayer({ id: 'pins-ev', type: 'circle', source: 'pois', 'source-layer': 'pois', filter: ['all', ['==', ['get', 'cat'], 'ev'], ladFilter] as unknown as maplibregl.FilterSpecification, layout: { visibility: 'none' },
    paint: { 'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, 4, 15, 7], 'circle-color': EV_COLOR, 'circle-opacity': 0.9, 'circle-stroke-color': '#fff', 'circle-stroke-width': 1 } });
  const AMEN_CATS = ['food_drink', 'supermarket', 'convenience', 'leisure', 'playground', 'worship'];
  m.addLayer({ id: 'pins-amenities', type: 'circle', source: 'pois', 'source-layer': 'pois', filter: ['all', ['in', ['get', 'cat'], ['literal', AMEN_CATS]], ladFilter] as unknown as maplibregl.FilterSpecification, layout: { visibility: 'none' },
    paint: { 'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, 3, 15, 6], 'circle-color': AMEN_COLOR, 'circle-opacity': 0.85, 'circle-stroke-color': '#fff', 'circle-stroke-width': 0.8 } });

  // ---- Entity point layers (schools / health / stations). Fixed radius, coloured by their own tone.
  const circle = (id: string, radius: maplibregl.DataDrivenPropertyValueSpecification<number>): void => {
    m.addLayer({ id, type: 'circle', source: id, layout: { visibility: 'none' }, paint: {
      'circle-radius': radius, 'circle-color': ['get', 'color'], 'circle-opacity': 0.9,
      'circle-stroke-color': '#ffffff', 'circle-stroke-width': 1.5,
    } });
  };
  circle('schools', 6); circle('health', 6); circle('stations', 7);

  // ---- Crime: a density HEATMAP (owner: heat, not dot spam) with the anonymised clusters as soft
  // markers on top for click/hover, weighted by cluster count. ALMANAC-4 D4: the old weight ramp mapped
  // 0..60 → 0..1, so a low-count cluster (weight ~1) rendered as ~0 and the map read as 2 faint dots.
  // Now weight 1 already carries real heat, and radius/intensity are higher; when the district is sparse
  // (<8 clusters) the wash + the markers are boosted further so Safety's map is never near-empty.
  const sparse = !!data.flags?.crimeSparse;
  // CROWDING (owner 2026-08-23: "blurred blobs crossing each other, 0 usable info"). The radius above was
  // a FIXED px kernel, so the busier the district the worse it read. Camden: 578 heat points, every one
  // weight 1, inside a ~430px pane — each 37px kernel covered ~9% of the pane, 578 of them overlapped,
  // the summed density clamped to the top of the ramp almost everywhere, and the borough rendered as one
  // amorphous brown blob. (The old numbers were pushed UP by ALMANAC-4 D4 to fix the opposite complaint —
  // "the map read as 2 faint dots" — on SPARSE rural districts; that over-corrects for dense urban ones.)
  //
  // So scale the kernel to the point count: `crowd` shrinks the radius as points pile up, which is what
  // lets neighbouring clusters stay separate instead of merging into a wash. Sparse districts are
  // untouched (crowd == 1 below ~70 points), so ALMANAC-4 D4's fix still holds where it was needed.
  const heatPts = data.crimeHeat?.length ?? 0;
  // ALMANAC-6 B: the heat reads the whole-district `crimeHeat` point source (spreads across the district);
  // the clickable `crime` clusters below stay the labelled hotspots. Each heat point is weight 1, so the
  // density is driven by where crime actually concentrates across the whole district, not a 5-point ramp.
  m.addLayer({ id: 'crime-heat', type: 'heatmap', source: 'crimeHeat', maxzoom: 16, layout: { visibility: 'none' }, paint: {
    'heatmap-weight': heatWeight(sparse) as maplibregl.DataDrivenPropertyValueSpecification<number>,
    'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 9, sparse ? 1.1 : 0.9, 15, sparse ? 2.0 : 1.6],
    'heatmap-radius': heatRadius(heatPts, 9, sparse ? 34 : 24, 15, sparse ? 70 : 54) as maplibregl.DataDrivenPropertyValueSpecification<number>,
    'heatmap-opacity': 0.78,
    // Start colouring LATER (0.30, was 0.20). The faint tail of every kernel was painting, which is what
    // produced the soft blurred edges with no discernible boundary between one cluster and the next.
    'heatmap-color': ['interpolate', ['linear'], ['heatmap-density'],
      0, 'rgba(0,0,0,0)', 0.30, 'rgba(240,215,182,0.50)', 0.62, 'rgba(205,115,67,0.85)', 1, '#9c4a26'],
  } });
  m.addLayer({ id: 'crime', type: 'circle', source: 'crime', layout: { visibility: 'none' }, paint: {
    'circle-radius': ['interpolate', ['linear'], ['coalesce', ['to-number', ['get', 'weight']], 1], 1, sparse ? 9 : 6, 200, sparse ? 22 : 16],
    'circle-color': '#c96f4a', 'circle-opacity': sparse ? 0.32 : 0.22, 'circle-stroke-color': '#c96f4a', 'circle-stroke-width': 1,
  } });

  // ALMANAC-4 D6: hover = a quick label; CLICK = a sticky popover with the key fact + "Open its page →"
  // wherever an entity page exists (schools / care / stations carry an href; EV/amenity tile pins show
  // name + category only — no dead link). "The big idea": no marker is inert.
  const hoverPopup = new maplibregl.Popup({ closeButton: false, closeOnClick: false, offset: 10 });
  const clickPopup = new maplibregl.Popup({ closeButton: true, closeOnClick: true, offset: 12, className: 'rm-popup', maxWidth: '240px' });
  for (const id of PIN_LAYER_IDS) {
    m.on('mouseenter', id, () => { m.getCanvas().style.cursor = 'pointer'; });
    m.on('mouseleave', id, () => { m.getCanvas().style.cursor = ''; hoverPopup.remove(); });
    m.on('mousemove', id, (e) => {
      const f = e.features?.[0]; if (!f) return;
      const g = f.geometry as GeoJSON.Point; const pr = f.properties as { name?: string; sub?: string; cat?: string };
      const sub = pr.sub || (pr.cat ? String(pr.cat).replace(/_/g, ' ') : '');
      hoverPopup.setLngLat(g.coordinates as [number, number])
        .setHTML(`<b>${esc(pr.name ?? '')}</b>${sub ? `<br>${esc(sub)}` : ''}`).addTo(m);
    });
    m.on('click', id, (e) => {
      const f = e.features?.[0]; if (!f) return;
      const g = f.geometry as GeoJSON.Point; const pr = f.properties as { name?: string; sub?: string; cat?: string; href?: string };
      hoverPopup.remove();
      const sub = pr.sub || (pr.cat ? String(pr.cat).replace(/_/g, ' ') : '');
      const href = pr.href ? String(pr.href) : '';
      const link = href ? `<a class="rm-open" href="${esc(href)}">Open its page →</a>` : '';
      clickPopup.setLngLat(g.coordinates as [number, number])
        .setHTML(`<b>${esc(pr.name ?? '')}</b>${sub ? `<div class="rm-sub">${esc(sub)}</div>` : ''}${link}`).addTo(m);
    });
  }
  // Choropleths (rule 6): hover = name+value, click = select (dark outline) + legend marker + a popover
  // linking to that child's own report. Price reads its neighbourhood; deprivation reads the LSOA's decile.
  for (const id of CHORO_IDS) {
    m.on('mouseenter', id, () => { m.getCanvas().style.cursor = 'pointer'; });
    m.on('mouseleave', id, () => { m.getCanvas().style.cursor = ''; hoverPopup.remove(); });
    m.on('mousemove', id, (e) => {
      const f = e.features?.[0]; if (!f) return;
      const code = String((f.properties as { area_code?: string }).area_code ?? f.id ?? '');
      const info = readChoro(id, code, f.properties as Record<string, unknown>);
      if (!info) return;
      hoverPopup.setLngLat(e.lngLat).setHTML(`<b>${esc(info.name)}</b><br>${esc(info.value)}`).addTo(m);
    });
    m.on('click', id, (e) => {
      const f = e.features?.[0]; if (!f) return;
      const code = String((f.properties as { area_code?: string }).area_code ?? f.id ?? '');
      const info = readChoro(id, code, f.properties as Record<string, unknown>);
      if (!info) return;
      selectArea(info.codes, `${info.value} · ${info.name}`);
      hoverPopup.remove();
      const link = info.path ? `<a class="rm-open" href="${esc(info.path)}">Open ${esc(info.name)} →</a>` : '';
      clickPopup.setLngLat(e.lngLat).setHTML(`<b>${esc(info.name)}</b><div class="rm-sub">${esc(info.value)}</div>${link}`).addTo(m);
    });
  }

  // GEOMETRY-RUN Chunk C — the street's sale pins. Hover = a compact "sold £X in YYYY"; click = a sticky
  // popover with the real address + last sold + "Plot Check →" (from saleMeta, keyed by uprn) — or, for a
  // pin matched only spatially (uprn not in the street history), the honest tile facts (year + price band).
  // No pin is inert (the no-dead-markers rule).
  if (m.getLayer('street-sales')) {
    const BANDS = ['under £150k', '£150–250k', '£250–400k', '£400–600k', '£600k–1m', '£1m+'];
    const pinFacts = (pr: { u?: number; y?: number; b?: number }): { line: string; sub: string; href: string } => {
      const meta = DATA?.saleMeta?.[String(pr.u ?? '')];
      if (meta) {
        const sold = [meta.d ? `Sold ${String(meta.d).slice(0, 4)}` : (pr.y ? `Sold ${pr.y}` : ''), meta.p ? `£${Math.round(meta.p).toLocaleString('en-GB')}` : ''].filter(Boolean).join(' · ');
        return { line: meta.a ?? 'This home', sub: sold, href: meta.h ?? '' };
      }
      const bandLbl = typeof pr.b === 'number' && pr.b >= 0 && pr.b < BANDS.length ? BANDS[pr.b] : '';
      return { line: pr.y ? `Sold in ${pr.y}` : 'A recorded sale', sub: bandLbl, href: '' };
    };
    m.on('mouseenter', 'street-sales', () => { m.getCanvas().style.cursor = 'pointer'; });
    m.on('mouseleave', 'street-sales', () => { m.getCanvas().style.cursor = ''; hoverPopup.remove(); });
    m.on('mousemove', 'street-sales', (e) => {
      const f = e.features?.[0]; if (!f) return;
      const g = f.geometry as GeoJSON.Point; const info = pinFacts(f.properties as { u?: number; y?: number; b?: number });
      hoverPopup.setLngLat(g.coordinates as [number, number]).setHTML(`<b>${esc(info.line)}</b>${info.sub ? `<br>${esc(info.sub)}` : ''}`).addTo(m);
    });
    m.on('click', 'street-sales', (e) => {
      const f = e.features?.[0]; if (!f) return;
      const g = f.geometry as GeoJSON.Point; const info = pinFacts(f.properties as { u?: number; y?: number; b?: number });
      hoverPopup.remove();
      const link = info.href ? `<a class="rm-open" href="${esc(info.href)}">Plot Check →</a>` : '';
      clickPopup.setLngLat(g.coordinates as [number, number]).setHTML(`<b>${esc(info.line)}</b>${info.sub ? `<div class="rm-sub">${esc(info.sub)}</div>` : ''}${link}`).addTo(m);
    });
  }
}

// Resolve a clicked/hovered choropleth feature → a friendly name + value + the LSOA codes to outline +
// the child's own-report path (D6) when we hold it.
function readChoro(layer: string, code: string, props: Record<string, unknown>): { name: string; value: string; codes: string[]; path?: string | null } | null {
  // FINAL-PUSH Fix 5: every district LSOA carries its report-unit name + scoped path (lsoaMeta), so a
  // click names + links ANY polygon — a named neighbourhood (nbByLsoa, priced), or a singleton/unpriced
  // LSOA (lsoaMeta). No more anonymous "This neighbourhood" that reads as the page's own area.
  const meta = DATA?.lsoaMeta?.[code];
  if (layer === 'nbhd-fill') {
    const n = nbByLsoa[code];
    const baked = Number(props.median_price);
    // Show the report UNIT'S OWN median — the exact figure its page reports — so the popover never
    // contradicts the page: the neighbourhood aggregate (n.price) for a member LSOA, else the unit
    // median threaded into lsoaMeta (covers singleton LSOAs whose tile-baked median_price differs from
    // their prices-theme median — the Kippington £782k-tile vs £612.5k-page case). Baked is a last resort.
    const unitMedian = (n && n.price != null) ? n.price : (meta && meta.median != null ? meta.median : null);
    const price = unitMedian != null ? unitMedian : (isFinite(baked) && baked > 0 ? baked : null);
    const name = n?.name ?? meta?.name ?? 'this area';
    const path = n?.path ?? meta?.path ?? null;
    const codes = n?.lsoas ?? [code];
    // Only if we could not hold the unit's own median and fell back to a single member LSOA's baked tile
    // value under a MULTI-LSOA unit is the figure one pocket, not the unit's — label it so ("· this
    // pocket", with the unit name always shown adjacent). A singleton unit keeps plain "£X median".
    const span = n ? n.lsoas.length : (path ? unitLsoaCount[path] ?? 1 : 1);
    const isPocket = price != null && unitMedian == null && span > 1;
    const value = price == null ? 'no price data'
      : isPocket ? `${gbpK(price)} · this pocket`
      : `${gbpK(price)} median`;
    return { name, value, codes, path };
  }
  // imd-fill — read the ACTIVE view (headline IMD from the tile, or the held sub-domain decile).
  const n = nbByLsoa[code];
  const name = n?.name ?? meta?.name ?? 'this area';
  const path = n?.path ?? meta?.path ?? null;
  const codes = n?.lsoas ?? [code];
  // DEMPROP-A3: a continuous demographic view shows the raw figure, not a decile.
  if (isDemogView(imdView)) {
    const dv = DATA?.demogViews?.[imdView];
    const v = dv ? Number(dv[code]) : NaN;
    if (!isFinite(v)) return { name, value: `no ${DEMOG_META[imdView].chip.toLowerCase()} data`, codes, path };
    return { name, value: DEMOG_META[imdView].fmt(v), codes, path };
  }
  const sub = imdView !== 'imd' ? DATA?.imdViews?.[imdView] : null;
  const d = sub ? Number(sub[code]) : Number(props.imd_decile);
  if (!isFinite(d) || d <= 0) { return { name, value: `no ${imdView === 'imd' ? 'deprivation' : IMD_VIEW_LABELS[imdView].toLowerCase()} data`, codes, path }; }
  const lbl = imdView === 'imd' ? 'IMD decile' : `${IMD_VIEW_LABELS[imdView]} decile`;
  return { name, value: `${lbl} ${Math.round(d)} of 10`, codes, path };
}

function selectArea(codes: string[], label: string): void {
  if (!map) return;
  selection = label;
  if (map.getLayer('sel-line')) map.setFilter('sel-line', ['in', ['get', 'area_code'], ['literal', codes]] as unknown as maplibregl.FilterSpecification);
  renderLegend();
}

function forEachCoord(geom: GeoJSON.Geometry, cb: (lng: number, lat: number) => void): void {
  const walk = (c: unknown): void => {
    if (Array.isArray(c) && typeof c[0] === 'number' && typeof c[1] === 'number') { cb(c[0], c[1]); return; }
    if (Array.isArray(c)) for (const x of c) walk(x);
  };
  if ('coordinates' in geom) walk((geom as { coordinates: unknown }).coordinates);
}

function esc(s: string): string { const d = document.createElement('div'); d.textContent = s; return d.innerHTML; }

// ---- The legend card (bottom of the map card). Per tab: choropleth ramp with real break values, EA-style
// bands, or a pin key. Updated on tab change and on a click-to-read selection.
function swatchRamp(colours: string[]): string { return `<div class="lg-ramp">${colours.map((c) => `<i style="background:${c}"></i>`).join('')}</div>`; }
function band(color: string, text: string, round = false): string {
  return `<div class="lg-band"><i style="background:${color}${round ? ';border-radius:50%' : ''}"></i>${text}</div>`;
}
// GEOMETRY-RUN reviewer fix 2: the price choropleth always shades NEIGHBOURHOODS (LSOA report-units), but on
// a street page it sits under the highlighted street + its sale pins, so a bare "Neighbourhood median price"
// reads as the street's own figure. Title per grain (mirrors the GRAINS nouns): a street page frames the
// same neighbourhood ramp as context near the street; every other grain keeps the plain neighbourhood title.
function priceLegendTitle(): string {
  return DATA?.self?.kind === 'street' ? 'Neighbourhood prices near this street' : 'Neighbourhood median price';
}
function legendFor(kind: LegendKind): string {
  const sel = selection ? `<span class="lg-sel">${esc(selection)}</span>` : '';
  switch (kind) {
    case 'price': {
      const breaks = DATA?.price?.breaks ?? [];
      const scale = breaks.length
        ? `<div class="lg-scale"><span>cheaper</span>${breaks.map(gbpK).map((b) => `<span>${b}</span>`).join('')}<span>dearer</span></div>`
        : '';
      // Reviewer fix 1: the "Streets by median" mode reshades to per-street medians → name that.
      // Reviewer fix 2: otherwise title per grain — a street page shades the wider district's neighbourhoods
      // as context UNDER the highlighted street + its pins, so "Neighbourhood prices near this street" reads
      // honestly (the ramp is still neighbourhood medians) instead of a bare "Neighbourhood median price"
      // that looks like the street's own figure.
      const title = streetsByMedian ? 'Street median price · residential sales only' : priceLegendTitle();
      return `<div class="lg-ttl"><span>${title}</span>${sel}</div>${swatchRamp(priceRamp)}${scale}`
        + `<div class="lg-note">Equal-size groups (quantiles) · <i class="lg-nodata"></i> no data · HM Land Registry · click a neighbourhood</div>`;
    }
    case 'imd':
      // DEMPROP-A3: a continuous demographic view gets a lower→higher value scale, not the decile scale.
      if (isDemogView(imdView)) {
        return `<div class="lg-ttl"><span>${esc(DEMOG_META[imdView].legend)}</span>${sel}</div>${swatchRamp(RAMP_CLAY)}`
          + `<div class="lg-scale"><span>lower</span><span>higher</span></div>`
          + `<div class="lg-note">Equal-size groups (quantiles) · <i class="lg-nodata"></i> no data where absent · click a neighbourhood</div>`;
      }
      return `<div class="lg-ttl"><span>${esc(IMD_LEGEND_TITLE[imdView] ?? IMD_LEGEND_TITLE.imd)}</span>${sel}</div>${swatchRamp(RAMP_CLAY)}`
        + `<div class="lg-scale"><span>least deprived (10)</span><span>most deprived (1)</span></div>`
        + `<div class="lg-note"><i class="lg-nodata"></i> no data where absent · English Indices of Deprivation 2019 · click a neighbourhood</div>`;
    case 'crime':
      return `<div class="lg-ttl"><span>Crime density</span></div>`
        + `<div class="lg-ramp" style="background:linear-gradient(90deg,${RAMP_CLAY[0]},${RAMP_CLAY[2]},#9c4a26)"></div>`
        + `<div class="lg-scale"><span>fewer</span><span>more</span></div>`
        // Owner 2026-08-23: some forces record crimes against their police station, so a station shows
        // as a genuine-looking hotspot. Said here, on the legend, because this is where the heat is read.
        + `<div class="lg-note">Anonymised “on or near” clusters — density, not addresses. Some forces log crimes at their police station, so a station can look like a hotspot · police.uk</div>`;
    case 'environment':
      return `<div class="lg-ttl"><span>Flood risk &amp; green space</span></div><div class="lg-bands">`
        + band(FLOOD3, 'Zone 3 — high chance from rivers/sea')
        + band(FLOOD2, 'Zone 2 — medium chance')
        + band(GREEN_FILL, 'Green space — parks &amp; playing fields')
        + `</div><div class="lg-note">Environment Agency · OS Open Greenspace</div>`;
    case 'amenities':
      return `<div class="lg-ttl"><span>Shops, amenities &amp; green space</span></div><div class="lg-bands">`
        + band(AMEN_COLOR, 'Shops, food &amp; leisure', true)
        + band(GREEN_FILL, 'Green space')
        + `</div><div class="lg-note">OpenStreetMap · OS Open Greenspace</div>`;
    case 'schools':
      return `<div class="lg-ttl"><span>Schools by Ofsted grade</span></div><div class="lg-bands">`
        + band(GOOD, 'Good / Outstanding', true)
        + band(MID, 'Requires improvement', true)
        + band(BAD, 'Inadequate', true)
        + `</div><div class="lg-note">Click a pin for the school · Ofsted</div>`;
    case 'health':
      return `<div class="lg-ttl"><span>Health services</span></div>`
        + `<div class="lg-note">GP surgeries, dentists &amp; pharmacies — click a pin · NHS / CQC</div>`;
    case 'transport':
      return `<div class="lg-ttl"><span>Getting around</span></div><div class="lg-bands">`
        + band(INK, 'Rail stations', true)
        + band(EV_COLOR, 'EV charge points', true)
        + `</div><div class="lg-note">Click a station for its commute times</div>`;
  }
}
function renderLegend(): void {
  const el = document.getElementById('report-map-legend'); if (!el) return;
  el.innerHTML = legendFor(PRESETS[currentTab]?.legend ?? 'price');
}

// ---- ALMANAC-4 D2: per-tab quick controls (layer filters). A chip exists only if its layer has data in
// this district (honest-data rule). Radio groups (Health kind, Schools phase, Schools Ofsted); multi
// toggles default-all-on (Getting-around, Environment, Amenities). View switchers (D2b) ship only when a
// second choropleth view has per-neighbourhood data — today only IMD exists, so Who-lives-here shows no
// switcher (documented; deprivation is the single guaranteed view).
const AMEN_GROUPS: { label: string; cats: string[] }[] = [
  { label: 'Eat & drink', cats: ['food_drink'] },
  { label: 'Shops', cats: ['supermarket', 'convenience'] },
  { label: 'Leisure', cats: ['leisure', 'playground', 'worship'] },
];
function setHealthFilter(kind: string): void {
  if (!map?.getLayer('health')) return;
  map.setFilter('health', kind && kind !== 'all' ? (['==', ['get', 'kind'], kind] as unknown as maplibregl.FilterSpecification) : null);
}
function setSchoolFilter(phase: string, ograde: string): void {
  if (!map?.getLayer('schools')) return;
  const conds: unknown[] = ['all'];
  if (phase && phase !== 'all') conds.push(['==', ['get', 'phase'], phase]);
  if (ograde === 'out') conds.push(['==', ['get', 'ograde'], 'out']);
  else if (ograde === 'goodplus') conds.push(['in', ['get', 'ograde'], ['literal', ['good', 'out']]]);
  map.setFilter('schools', conds.length > 1 ? (conds as unknown as maplibregl.FilterSpecification) : null);
}
function setAmenFilter(cats: string[]): void {
  if (!map?.getLayer('pins-amenities') || !DATA) return;
  map.setFilter('pins-amenities', ['all', ['in', ['get', 'cat'], ['literal', cats]], ['==', ['get', 'lad_code'], DATA.code]] as unknown as maplibregl.FilterSpecification);
}
function chipBtn(label: string, on: boolean): HTMLButtonElement {
  const b = document.createElement('button'); b.type = 'button'; b.className = 'mapctl' + (on ? ' on' : ''); b.textContent = label; return b;
}
function radioGroup(defs: [string, string][], initial: string, onPick: (v: string) => void): HTMLElement {
  const g = document.createElement('div'); g.className = 'mapctl-group';
  for (const [label, v] of defs) {
    const b = chipBtn(label, v === initial);
    b.addEventListener('click', () => { g.querySelectorAll('.mapctl').forEach((x) => x.classList.remove('on')); b.classList.add('on'); onPick(v); });
    g.appendChild(b);
  }
  return g;
}
function renderControls(tab: string): void {
  const el = document.getElementById('report-map-controls'); if (!el) return;
  el.innerHTML = '';
  if (!map || !DATA) { el.hidden = true; return; }
  const f = DATA.flags ?? {};
  // GEOMETRY-RUN Chunk C3 — the price tabs offer a "Streets by median" toggle wherever the assembler shipped
  // the streets layer (uk-streets carries our per-street median). It shades every matched centreline in the
  // district on the same clay ramp as the choropleth — the area → street → sale grain ladder, made literal.
  if ((tab === 'overview' || tab === 'cost') && DATA.streetsLayer && map.getLayer('nbhd-streets')) {
    const g = document.createElement('div'); g.className = 'mapctl-group';
    const b = chipBtn('Streets by median', false);
    b.addEventListener('click', () => {
      const on = b.classList.toggle('on');
      streetsByMedian = on;
      map!.setLayoutProperty('nbhd-streets', 'visibility', on ? 'visible' : 'none');
      // Reviewer fix: at 1px on the same ramp the shaded streets vanished into the choropleth. On, fade the
      // neighbourhood fill right back and thicken the centrelines so the street grain reads; restore on off.
      if (map!.getLayer('nbhd-fill')) map!.setPaintProperty('nbhd-fill', 'fill-opacity', on ? NBHD_FILL_FADED : NBHD_FILL_OPACITY);
      map!.setPaintProperty('nbhd-streets', 'line-width', on ? STREETS_W_EMPH : STREETS_W_DEFAULT);
      renderLegend(); // swap the legend title to "Street median price · residential sales only" (and back)
    });
    g.appendChild(b); el.appendChild(g);
  }
  if (tab === 'who-lives-here') {
    // ALMANAC-5 D3: the census-style view switcher — Deprivation (IMD, default) + any sub-domain we hold
    // real per-LSOA deciles for (income/employment/education/health). No dead chips: a view appears only
    // when its data is present. A single guaranteed view (just IMD) shows no switcher (would be noise).
    setImdView('imd');
    const views = DATA.imdViews ?? {};
    const dviews = DATA.demogViews ?? {};
    const order = ['imd', 'income', 'employment', 'education', 'health'];
    const defs = order
      .filter((v) => v === 'imd' || (views[v] && Object.keys(views[v]).length))
      .map((v) => [IMD_VIEW_LABELS[v], v] as [string, string]);
    // DEMPROP-A3: append the continuous age/tenure/density views where their data is present (dormant
    // until the census backfill — no dead chips, exactly like the IMD sub-domain views).
    for (const v of DEMOG_VIEWS) if (dviews[v] && Object.keys(dviews[v]).length) defs.push([DEMOG_META[v].chip, v]);
    if (defs.length > 1) el.appendChild(radioGroup(defs, 'imd', setImdView));
  } else if (tab === 'health') {
    setHealthFilter('all');
    const kinds = new Set((DATA.health ?? []).map((p) => p.kind).filter(Boolean));
    const defs = ([['All', 'all'], ['GPs', 'gp'], ['Dentists', 'dentist'], ['Pharmacies', 'pharmacy']] as [string, string][]).filter(([, k]) => k === 'all' || kinds.has(k));
    if (defs.length > 2) el.appendChild(radioGroup(defs, 'all', setHealthFilter));
  } else if (tab === 'schools') {
    setSchoolFilter('all', 'all');
    const st = { phase: 'all', ograde: 'all' };
    const phases = new Set((DATA.schools ?? []).map((p) => p.phase).filter(Boolean));
    if (phases.has('primary') || phases.has('secondary')) {
      const defs = ([['All', 'all'], ['Primary', 'primary'], ['Secondary', 'secondary']] as [string, string][]).filter(([, v]) => v === 'all' || phases.has(v));
      el.appendChild(radioGroup(defs, 'all', (v) => { st.phase = v; setSchoolFilter(st.phase, st.ograde); }));
    }
    const grades = new Set((DATA.schools ?? []).map((p) => p.ograde).filter((g) => g && g !== 'other'));
    if (grades.size) {
      const defs: [string, string][] = [['All Ofsted', 'all'], ['Good+', 'goodplus'], ['Outstanding', 'out']];
      el.appendChild(radioGroup(defs, 'all', (v) => { st.ograde = v; setSchoolFilter(st.phase, st.ograde); }));
    }
  } else if (tab === 'getting-around') {
    const g = document.createElement('div'); g.className = 'mapctl-group';
    ([['Stations', ['stations'], (DATA.stations ?? []).length > 0], ['EV charging', ['pins-ev'], !!f.hasEv]] as [string, string[], boolean][])
      .filter((c) => c[2]).forEach(([label, layers]) => {
        const b = chipBtn(label, true);
        b.addEventListener('click', () => { const on = b.classList.toggle('on'); layers.forEach((l) => map!.getLayer(l) && map!.setLayoutProperty(l, 'visibility', on ? 'visible' : 'none')); });
        g.appendChild(b);
      });
    if (g.children.length > 1) el.appendChild(g);
  } else if (tab === 'environment') {
    const g = document.createElement('div'); g.className = 'mapctl-group';
    // ALMANAC-6 C: the Transport-noise chip appears only where the area holds mapped Defra bands
    // (hasNoise) — never a dead control. It toggles the pre-wired ov-noise corridor layer.
    ([['Flood zones', ['ov-floodfz3', 'ov-floodfz2'], !!f.hasFlood], ['Green spaces', ['ov-greenspace'], !!f.hasGreen], ['Transport noise', ['ov-noise'], !!f.hasNoise]] as [string, string[], boolean][])
      .filter((c) => c[2]).forEach(([label, layers]) => {
        const b = chipBtn(label, true);
        b.addEventListener('click', () => { const on = b.classList.toggle('on'); layers.forEach((l) => map!.getLayer(l) && map!.setLayoutProperty(l, 'visibility', on ? 'visible' : 'none')); });
        g.appendChild(b);
      });
    if (g.children.length > 1) el.appendChild(g);
  } else if (tab === 'amenities') {
    const avail = AMEN_GROUPS.filter((_, i) => [f.amenFood, f.amenShops, f.amenLeisure][i]);
    if (avail.length > 1) {
      const active = new Set<string>(); avail.forEach((a) => a.cats.forEach((c) => active.add(c)));
      setAmenFilter([...active]);
      const g = document.createElement('div'); g.className = 'mapctl-group';
      avail.forEach((a) => {
        const b = chipBtn(a.label, true);
        b.addEventListener('click', () => { const on = b.classList.toggle('on'); a.cats.forEach((c) => (on ? active.add(c) : active.delete(c))); setAmenFilter([...active]); });
        g.appendChild(b);
      });
      el.appendChild(g);
    }
  }
  el.hidden = el.children.length === 0;
}

// FINAL-PUSH Fix 2: the chip must state EXACTLY what the map shows at this grain — never "the district, one
// level down" on a sub-grain page. Only the price-choropleth tabs (overview/cost) carry a grain-specific
// context suffix; every other tab's chip ("crime density", "schools — Ofsted grade", …) is grain-neutral
// and stays as-is. Honest about geometry: a neighbourhood outlines its own area, a street shows a location
// marker (the sale history is the table, not map pins), a sector shows its position.
function chipText(tab: string, dflt: string): string {
  const s = DATA?.self;
  if (!s || (tab !== 'overview' && tab !== 'cost')) return dflt;
  const nm = esc(s.label ?? DATA!.name);
  const parent = esc(DATA!.name); // for a sub-grain, data.name is the parent LAD (the map keys to it)
  // GEOMETRY-RUN: a matched street shows its highlighted centreline + its sold homes pinned; an unmatched
  // street (streetDegraded) falls back to the honest centroid + "history is in the table" wording.
  if (s.kind === 'street') return (s.streetSlug && !streetDegraded)
    ? `<b>${nm}</b> highlighted — every sold home on it pinned`
    : `<b>${nm}</b> — its location; the sale history is in the table below`;
  // GEOMETRY-RUN Chunk D: a sector with its member-LSOA footprint outlines its own area; else the position.
  if (s.kind === 'sector') return (s.lsoas && s.lsoas.length)
    ? `<b>${nm}</b>’s area outlined — neighbourhood prices across ${parent}`
    : `<b>neighbourhood prices</b> — where <b>${nm}</b> sits in ${parent}`;
  return (s.lsoas && s.lsoas.length)
    ? `<b>${nm}</b>’s area outlined — prices across ${parent}’s neighbourhoods`
    : `<b>neighbourhood prices</b> — <b>${nm}</b> among ${parent}’s`;
}

let currentTab = 'overview';
function applyPreset(tab: string): void {
  const changed = tab !== currentTab;
  currentTab = tab;
  const preset = PRESETS[tab] ?? PRESETS['overview'];
  const chip = document.getElementById('report-map-chip');
  if (chip) chip.innerHTML = 'Map · ' + chipText(tab, preset.chip);
  // A tab change clears any click-to-read selection (it belonged to the previous layer).
  if (changed) { selection = null; if (map?.getLayer('sel-line')) map.setFilter('sel-line', ['in', ['get', 'area_code'], ['literal', []]] as unknown as maplibregl.FilterSpecification); }
  // Reviewer fix 1: re-presetting drops the "Streets by median" emphasis (the control is rebuilt off below,
  // and nbhd-streets is hidden by the authoritative swap), so a stale faded fill / thick lines never linger.
  streetsByMedian = false;
  renderLegend();
  // Toggle visibility per-layer whenever the LAYER exists — NOT gated on isStyleLoaded() (which stays
  // false while any source is still loading, and previously suppressed the whole choropleth even though
  // setLayoutProperty on an existing layer is perfectly safe mid-load). ALMANAC-3 root-cause #2.
  if (!map) return;
  // Authoritative swap: every toggleable layer is set explicitly from THIS preset, so the previous tab's
  // layers are always cleared (no bleed-through). Idempotent — safe to call repeatedly.
  const want = new Set(preset.layers);
  for (const id of TOGGLE_IDS) if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', want.has(id) ? 'visible' : 'none');
  // Restore the choropleth fill + street width to their defaults (undo any "Streets by median" emphasis).
  if (map.getLayer('nbhd-fill')) map.setPaintProperty('nbhd-fill', 'fill-opacity', NBHD_FILL_OPACITY);
  if (map.getLayer('nbhd-streets')) map.setPaintProperty('nbhd-streets', 'line-width', STREETS_W_DEFAULT);
  // ALMANAC-4 D2: rebuild the per-tab quick controls (resets their filters to default-all for this tab).
  renderControls(tab);
}

// Public API for the inline hydrate script (tabs + neighbourhood rows drive the map; the map never
// navigates the page).
interface DistrictMapApi { setLayer(tab: string): void; flyTo(code: string): void; panTo(lng: number, lat: number, label?: string): void; getMap(): maplibregl.Map | null }
const api: DistrictMapApi = {
  setLayer(tab) { applyPreset(tab); },
  // Debug/verification handle — lets an automated check read live layer + camera state (the map instance
  // otherwise lives in a closure). Harmless read-only accessor.
  getMap() { return map; },
  // ALMANAC-6 D: an amenities-table row click pans to that place + drops a marker popover, so the row
  // and the map agree (the "row click → pin highlight" contract). Cheap: no query, just a fly + popup.
  panTo(lng, lat, label) {
    if (!map || !Number.isFinite(lng) || !Number.isFinite(lat)) return;
    map.flyTo({ center: [lng, lat], zoom: 15, duration: 700 });
    if (label) new maplibregl.Popup({ closeButton: true, offset: 10, className: 'rm-popup' }).setLngLat([lng, lat]).setHTML(`<b>${esc(label)}</b>`).addTo(map);
  },
  flyTo(code) {
    if (!map) return;
    // A neighbourhood row carries its NB_… code; fly to it over its member LSOA polygons (the tile is
    // LSOA-keyed). Fall back to a direct area_code match for any code the tile holds itself.
    const members = nbByCode[code]?.lsoas?.length ? nbByCode[code].lsoas : [code];
    const filter = ['in', ['get', 'area_code'], ['literal', members]] as unknown as maplibregl.FilterSpecification;
    const feats = map.querySourceFeatures('areas', { sourceLayer: 'areas', filter });
    const b = new maplibregl.LngLatBounds(); let any = false;
    for (const f of feats) forEachCoord(f.geometry, (lng, lat) => { b.extend([lng, lat]); any = true; });
    if (any) map.fitBounds(b, { padding: 60, duration: 800, maxZoom: 15 });
    // Also select it (dark outline + legend read) so a row click and a map click agree.
    const n = nbByCode[code];
    if (n) selectArea(n.lsoas.length ? n.lsoas : [code], n.price != null ? `${gbpK(n.price)} · ${n.name}` : n.name);
  },
};
(window as unknown as { __districtMap: DistrictMapApi }).__districtMap = api;

// Lazy start on idle — the map runtime + tiles must never gate first paint. Guarded so a re-run is safe.
function start(): void {
  if (started) return; const data = readData(); if (!data) return; started = true;
  // Sync to whatever tab is active now (hydrate may have switched it, or a #hash deep-link opened one)
  // so the very first layer set matches the open section, not a hardcoded default.
  const activeTab = document.querySelector<HTMLElement>('.tabs [role=tab].on')?.dataset.tab
    || (location.hash ? location.hash.slice(1) : '') || 'overview';
  currentTab = PRESETS[activeTab] ? activeTab : 'overview';
  boot(data);
}
// FINAL-PUSH Fix 3: start as soon as the SSR content has painted. The map is a PRIMARY element of the
// report card, so the old idle-deferral (requestIdleCallback timeout 2500 / setTimeout 1200) needlessly
// held the choropleth fills back by 1–2.5 s ("nothing at 3s"). The report content is server-rendered and
// already in the DOM, so a double-rAF (≈ after the first paint) kicks the map without gating that paint;
// a 300 ms macrotask backstop covers a background tab where rAF is throttled.
const kick = (): void => { if (!started) start(); };
if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => requestAnimationFrame(kick));
else window.setTimeout(kick, 50);
window.setTimeout(kick, 300);
